/**
 * Document model for the board.
 *
 * Every node is a plain, JSON-serialisable object. That is deliberate: the whole
 * document lives inside a Yjs Y.Map<NodeId, BoardNode> so any peer can merge
 * concurrent edits without a central authority. Keeping nodes flat (no class
 * instances, no nested Y types per node) means one node update is one CRDT
 * operation, which is what keeps 50-person boards responsive.
 */

export type NodeId = string;
export type UserId = string;

export interface Vec {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

export type StrokeStyle = 'solid' | 'dashed' | 'dotted';
export type FillStyle = 'solid' | 'none';
export type FontFamily = 'sans' | 'serif' | 'mono' | 'marker';
export type TextAlign = 'left' | 'center' | 'right';
export type VerticalAlign = 'top' | 'middle' | 'bottom';

export interface TextStyle {
  color: string;
  fontSize: number;
  fontFamily: FontFamily;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  align: TextAlign;
  valign: VerticalAlign;
  /** Multiplier of fontSize. */
  lineHeight: number;
}

export interface ShapeStyle {
  fill: string;
  fillStyle: FillStyle;
  stroke: string;
  strokeWidth: number;
  strokeStyle: StrokeStyle;
  /** Corner radius in px, only meaningful for rectangular geometry. */
  radius: number;
  shadow: boolean;
}

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

export type NodeType =
  | 'shape'
  | 'sticky'
  | 'text'
  | 'draw'
  | 'connector'
  | 'image'
  | 'frame'
  | 'group'
  | 'embed'
  | 'comment'
  | 'notebook';

export type ShapeKind =
  | 'rect'
  | 'roundRect'
  | 'ellipse'
  | 'triangle'
  | 'rightTriangle'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'octagon'
  | 'star'
  | 'parallelogram'
  | 'trapezoid'
  | 'chevron'
  | 'arrowRight'
  | 'arrowLeft'
  | 'cross'
  | 'cylinder'
  | 'cloud'
  | 'speechBubble'
  | 'heart'
  | 'halfCircle';

export interface BaseNode {
  id: NodeId;
  type: NodeType;
  /** Top-left of the un-rotated bounding box, in board coordinates. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Radians, clockwise, about the box centre. */
  rotation: number;
  /** Fractional index: lexicographic order equals paint order. */
  index: string;
  /** Enclosing frame or group, null when at the board root. */
  parentId: NodeId | null;
  opacity: number;
  locked: boolean;
  createdBy: UserId;
  createdAt: number;
  updatedAt: number;
  /** Optional hyperlink attached to the node. */
  link?: string | null;
  /** Hidden from the canvas, hit-testing and export; still present in the doc. */
  hidden?: boolean;
  /** Free-form labels, searchable from the search panel. */
  tags?: string[];
}

export interface ShapeNode extends BaseNode {
  type: 'shape';
  shape: ShapeKind;
  style: ShapeStyle;
  text: string;
  textStyle: TextStyle;
}

export interface StickyNode extends BaseNode {
  type: 'sticky';
  /** Sticky notes carry a palette entry rather than a full ShapeStyle. */
  fill: string;
  text: string;
  textStyle: TextStyle;
  /** Grow the note downwards instead of shrinking text when it overflows. */
  autoGrow: boolean;
}

export interface TextNode extends BaseNode {
  type: 'text';
  text: string;
  textStyle: TextStyle;
  /** When true, h tracks the measured text height. */
  autoHeight: boolean;
  background: string | null;
}

/** A single freehand sample: local x, local y, pressure 0..1. */
export type DrawPoint = [number, number, number];

export interface DrawNode extends BaseNode {
  type: 'draw';
  /** Points are local to (x, y) and scale with (w, h). */
  points: DrawPoint[];
  color: string;
  size: number;
  brush: 'pen' | 'marker' | 'highlighter';
  /** Closed strokes get filled, used by the shape-recognition assist. */
  closed: boolean;
}

export type ArrowHead =
  | 'none'
  | 'arrow'
  | 'triangle'
  | 'filledTriangle'
  | 'circle'
  | 'filledCircle'
  | 'diamond'
  | 'bar';

export type ConnectorRouting = 'straight' | 'elbow' | 'curved';

/** Normalised anchor inside a node local box; auto picks the nearest side. */
export type ConnectorAnchor = { kind: 'auto' } | { kind: 'fixed'; nx: number; ny: number };

export type ConnectorEndpoint =
  | { kind: 'point'; x: number; y: number }
  | { kind: 'node'; nodeId: NodeId; anchor: ConnectorAnchor };

export interface ConnectorNode extends BaseNode {
  type: 'connector';
  start: ConnectorEndpoint;
  end: ConnectorEndpoint;
  routing: ConnectorRouting;
  startHead: ArrowHead;
  endHead: ArrowHead;
  stroke: string;
  strokeWidth: number;
  strokeStyle: StrokeStyle;
  text: string;
  textStyle: TextStyle;
  /** Mid-point offsets that let a user drag a segment off the auto route. */
  bend: Vec[];
}

export interface ImageNode extends BaseNode {
  type: 'image';
  /** Asset id resolved through the asset store, or an absolute URL. */
  src: string;
  naturalW: number;
  naturalH: number;
  radius: number;
  /** 0..1 crop rectangle applied before drawing. */
  crop: Rect | null;
  /**
   * Optional freehand clip in normalised natural-image coordinates (0..1).
   * When set, only the interior of this polygon is visible inside the crop box.
   */
  mask?: Vec[] | null;
  alt: string;
}

export interface FrameNode extends BaseNode {
  type: 'frame';
  name: string;
  fill: string;
  /** Clip children to the frame bounds when rendering and exporting. */
  clip: boolean;
  /** Frames are the unit of presentation mode; this is their slide order. */
  slideIndex: number | null;
}

export interface GroupNode extends BaseNode {
  type: 'group';
  children: NodeId[];
}

export interface EmbedNode extends BaseNode {
  type: 'embed';
  url: string;
  provider: 'youtube' | 'vimeo' | 'figma' | 'gdocs' | 'iframe';
  title: string;
}

export interface CommentNode extends BaseNode {
  type: 'comment';
  threadId: string;
  resolved: boolean;
  /** Anchored to a node so the pin follows it around. */
  anchorNodeId: NodeId | null;
}

/** One freehand mark drawn on a notebook page, in page-local coordinates. */
export interface NotebookStroke {
  id: string;
  points: DrawPoint[];
  color: string;
  size: number;
  brush: DrawNode['brush'];
}

/** Image pasted or uploaded onto a notebook page, in page-local coordinates. */
export interface NotebookImage {
  id: string;
  src: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface NotebookPage {
  id: string;
  text: string;
  strokes: NotebookStroke[];
  /** Photos / pasted images on this page. Missing on older boards. */
  images?: NotebookImage[];
}

/**
 * A multi-page notebook that lives on the board. Double-click opens it so the
 * user can flip pages, type, and draw inside; the cover is what sits on the
 * canvas when it is closed.
 */
export type NotebookPaper = 'lined' | 'grid' | 'blank';

export interface NotebookNode extends BaseNode {
  type: 'notebook';
  title: string;
  pages: NotebookPage[];
  /** Index of the page shown when the notebook is open. */
  pageIndex: number;
  coverColor: string;
  /** Page ruling: lined (в линейку), grid (в клетку), or blank. */
  paper: NotebookPaper;
  /** @deprecated Prefer `paper`. Kept so older boards still open. */
  ruled?: boolean;
}

export type BoardNode =
  | ShapeNode
  | StickyNode
  | TextNode
  | DrawNode
  | ConnectorNode
  | ImageNode
  | FrameNode
  | GroupNode
  | EmbedNode
  | CommentNode
  | NotebookNode;

export type NodeOfType<T extends NodeType> = Extract<BoardNode, { type: T }>;

/** Nodes whose geometry is a plain box the user can resize from handles. */
export type BoxNode = Exclude<BoardNode, ConnectorNode>;

/** Nodes that render editable text through the shared text pipeline. */
export type TextualNode = ShapeNode | StickyNode | TextNode | ConnectorNode;

export function isTextual(node: BoardNode): node is TextualNode {
  return (
    node.type === 'shape' ||
    node.type === 'sticky' ||
    node.type === 'text' ||
    node.type === 'connector'
  );
}

export function isBox(node: BoardNode): node is BoxNode {
  return node.type !== 'connector';
}

// ---------------------------------------------------------------------------
// Collaboration
// ---------------------------------------------------------------------------

export interface DrawDraft {
  points: DrawPoint[];
  color: string;
  size: number;
  brush: DrawNode['brush'];
}

export interface Presence {
  userId: UserId;
  name: string;
  color: string;
  avatar?: string | null;
  /** Board-space cursor, null when the pointer left the canvas. */
  cursor: Vec | null;
  selection: NodeId[];
  /** Viewport rect, used for the participants list and follow mode. */
  viewport: Rect | null;
  tool: string;
  /** Stroke the peer is drawing right now, before it is committed. */
  draft?: DrawDraft | null;
  /** Live laser-pointer trail in board coordinates, newest point last. */
  laser?: Vec[] | null;
  /** Set while the user is speaking in the call attached to this board. */
  speaking?: boolean;
  micOn?: boolean;
  camOn?: boolean;
  /** Set while this peer is connected to the board's LiveKit call at all — distinct
   *  from `micOn`/`camOn`, which describe a call participant's device state and would
   *  otherwise be indistinguishable from "not on the call". */
  inCall?: boolean;
  /** Set while this peer is showing the frame deck (`PresentationBar`) — what
   *  the call's "follow the presenter" auto-engage watches for. */
  presenting?: boolean;
  lastActive: number;
}

/**
 * One line of in-call chat.
 *
 * The author's name and role are copied in rather than looked up when the
 * message is rendered: chat is a record of who said what at the time, and a
 * person renamed or moved to another role later should not retroactively
 * change what the transcript says.
 */
export interface CallChatMessage {
  id: string;
  /** Which call this was said in, so a panel can scope itself to the current one. */
  callId: string;
  userId: UserId;
  name: string;
  color: string;
  roleSlug: string | null;
  roleLabel: string | null;
  text: string;
  ts: number;
  /** Optional image attached to the message (uploaded asset URL or data URL). */
  imageUrl?: string | null;
  imageMime?: string | null;
  /**
   * Emoji → user ids who reacted. Kept on the message so peers sync reactions
   * the same way they sync the transcript.
   */
  reactions?: Record<string, string[]>;
}

export interface CommentMessage {
  id: string;
  threadId: string;
  authorId: UserId;
  authorName: string;
  body: string;
  createdAt: number;
  editedAt: number | null;
}

export interface BoardMeta {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /** Board-space background colour, drawn under the grid. */
  background: string;
  grid: 'none' | 'dots' | 'lines';
  snapToGrid: boolean;
  gridSize: number;
}
